// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";

import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import placeOrderForApproval from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/PlaceOrderForApproval";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import updateCurrentAccount from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateCurrentAccount";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { hidePrice } from "@sc/App/Store/Selectors/GeneralSelectors";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = getCurrentPromotionsDataView(state).value;
    const cart = cartState.value;
    return {
        cart,
        account: getCurrentAccountState(state).value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading,
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        placeOrderErrorMessage: state.pages.onePageCheckout.placeOrderErrorMessage,
        showPlaceOrderButton: canPlaceOrder(cart),
        showSubmitForApprovalOrderButton: canSubmitForApprovalOrder(cart),
        // XNG-Change
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
        // XNG-Change
        showTaxAndShipping: getSettingsCollection(state).cartSettings.showTaxAndShipping,
        isUpdatingOneTimeAddress: (state as CustomAppState).xCheckout.isUpdatingOneTimeAddress,
        hidePrices: hidePrice(state),
    };
};

// XNG-Change: from OnePageCheckoutReviewAndPlaceOrder widget
const mapDispatchToProps = {
    setIsPlacingOrder,
    placeOrderForApproval,
    updateCurrentAccount,
};

// XNG-Change
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OnePageCheckoutOrderSummaryStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    // XNG-Change
    notify?: InjectableCss;
    wrapper?: InjectableCss;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    actionsWrapper?: InjectableCss;
}

export const orderSummaryStyles: OnePageCheckoutOrderSummaryStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    cartTotalGridItem: { width: 12 },
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
};

const styles = orderSummaryStyles;

const OnePageCheckoutOrderSummary = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    setIsPlacingOrder,
    placeOrderForApproval,
    updateCurrentAccount,
    isPlacingOrder,
    placeOrderErrorMessage,
    showPlaceOrderButton,
    showSubmitForApprovalOrderButton,
    onePageCheckoutConfirmationPageUrl,
    history,
    account,
    // XNG-Change
    showTaxAndShipping,
    isUpdatingOneTimeAddress,
    hidePrices,
}: Props) => {
    // XNG-Change: from OnePageCheckoutReviewAndPlaceOrder widget
    const placeOrderClickHandler = () => {
        handleClick();
        setIsPlacingOrder({ isPlacingOrder: true });
    };

    const handleClick = () => {
        document.getElementById("onePageCheckout_payment_submit")?.click();
    };

    const submitForApprovalOrderClickHandler = () => {
        placeOrderForApproval({
            onSuccess: (cartId: string) => {
                // XNG-Change
                if (onePageCheckoutConfirmationPageUrl) {
                    history.push(`${onePageCheckoutConfirmationPageUrl}?cartId=${cartId}`);
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart.id) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
        });
    };
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_orderSummary" />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <CartTotalDisplay
                extendedStyles={styles.cartTotal}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
                // XNG-Change
                showTaxAndShipping={showTaxAndShipping}
                {...{ hidePrices }}
            />
            <StyledWrapper {...styles.actionsWrapper}>
                {showPlaceOrderButton && (
                    <Button
                        {...styles.placeOrderButton}
                        disabled={isLoading || isPlacingOrder || !cart?.canCheckOut || isUpdatingOneTimeAddress}
                        onClick={placeOrderClickHandler}
                        data-test-selector="checkoutReviewAndSubmit_placeOrder"
                        aria-label={translate("Place Order Request")}
                    >
                        {translate("Place Order Request")}
                    </Button>
                )}
                {showSubmitForApprovalOrderButton && (
                    <Button
                        {...styles.submitForApprovalButton}
                        disabled={isLoading || isPlacingOrder}
                        onClick={submitForApprovalOrderClickHandler}
                        data-test-selector="checkoutReviewAndSubmit_submitForApproval"
                        aria-label={translate("Submit For Approval")}
                    >
                        {translate("Submit For Approval")}
                    </Button>
                )}
            </StyledWrapper>
            {placeOrderErrorMessage && (
                <Notify type="danger" {...styles.notify}>
                    {placeOrderErrorMessage}
                </Notify>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Change
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutOrderSummary)),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "Order Summary",
    },
};

export default widgetModule;
